import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
    Box, Typography, Button, IconButton, Chip, CircularProgress,
    Drawer, TextField, MenuItem, Select, FormControl, InputLabel,
    Autocomplete, ToggleButton, ToggleButtonGroup, Tooltip, Paper, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Layout from './Layout';
import ProjectTaskService from '../services/ProjectTaskService';
import EmployeeService from '../services/EmployeeService';
import ProjectService from '../services/ProjectService';
import { useToast } from '../contexts/ToastContext';
import { AuthContext } from '../contexts/AuthContext';
import Roles from '../config/roles';
import ConfirmModal from './modal/ConfirmModal';

const STATUS_COLORS = {
    PLANNED:     { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
    IN_PROGRESS: { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },
    DONE:        { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0' },
    CANCELLED:   { bg: '#F8FAFC', text: '#94A3B8', border: '#E2E8F0' },
};

const STATUS_LABELS = {
    PLANNED: 'Planned',
    IN_PROGRESS: 'In Progress',
    DONE: 'Done',
    CANCELLED: 'Cancelled',
};

const fmt = (date) => date.toISOString().split('T')[0];
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const startOfWeek = (date) => { const d = new Date(date); const day = d.getDay(); d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day)); return d; };
const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const formatDisplayDate = (date) => date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const formatShortDate = (date) => date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const timeToMinutes = (t) => { if (!t) return null; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const tasksOverlap = (a, b) => {
    const s1 = timeToMinutes(a.startTime), e1 = timeToMinutes(a.endTime);
    const s2 = timeToMinutes(b.startTime), e2 = timeToMinutes(b.endTime);
    if (s1 == null || e1 == null || s2 == null || e2 == null) return false;
    return s1 < e2 && s2 < e1;
};
const getConflictIds = (empTasks) => {
    const ids = new Set();
    for (let i = 0; i < empTasks.length; i++)
        for (let j = i + 1; j < empTasks.length; j++)
            if (tasksOverlap(empTasks[i], empTasks[j])) { ids.add(empTasks[i].id); ids.add(empTasks[j].id); }
    return ids;
};

const EMPTY_FORM = { title: '', description: '', projectId: null, employeeIds: [], taskDate: fmt(new Date()), startTime: '', endTime: '', location: '', status: 'PLANNED' };
const DRAWER_WIDTH = 460;

export default function PlannerPage() {
    const { showToast } = useToast();
    const { role } = useContext(AuthContext);
    const isEmployee = role === Roles.USER;

    const [viewMode, setViewMode] = useState('day');
    const [currentDate, setCurrentDate] = useState(new Date());

    const [employees, setEmployees] = useState([]);
    const [projects, setProjects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editTask, setEditTask] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const [comments, setComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [commentSaving, setCommentSaving] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingCommentText, setEditingCommentText] = useState('');

    useEffect(() => {
        if (isEmployee) return;
        EmployeeService.getEmployees(0, 200).then(d => setEmployees(d?.content || [])).catch(() => {});
        ProjectService.getAllProjectsFlat().then(setProjects).catch(() => {});
    }, [isEmployee]);

    const loadTasks = useCallback(async () => {
        setLoading(true);
        try {
            let data;
            if (isEmployee) {
                if (viewMode === 'day') {
                    data = await ProjectTaskService.getMyTasks(fmt(currentDate));
                } else {
                    const monday = startOfWeek(currentDate);
                    data = await ProjectTaskService.getMyTasksRange(fmt(monday), fmt(addDays(monday, 6)));
                }
            } else {
                if (viewMode === 'day') {
                    data = await ProjectTaskService.getByDate(fmt(currentDate));
                } else {
                    const monday = startOfWeek(currentDate);
                    data = await ProjectTaskService.getByDateRange(fmt(monday), fmt(addDays(monday, 6)));
                }
            }
            setTasks(data || []);
        } catch { showToast('Failed to load tasks', 'error'); }
        finally { setLoading(false); }
    }, [viewMode, currentDate, showToast, isEmployee]);

    useEffect(() => { loadTasks(); }, [loadTasks]);

    const resetComments = () => {
        setComments([]);
        setNewComment('');
        setEditingCommentId(null);
        setEditingCommentText('');
    };

    const loadComments = async (taskId) => {
        setCommentsLoading(true);
        try {
            setComments(await ProjectTaskService.getComments(taskId));
        } catch { showToast('Failed to load comments', 'error'); }
        finally { setCommentsLoading(false); }
    };

    const openAdd = (date, employeeId) => {
        setEditTask(null);
        setForm({ ...EMPTY_FORM, taskDate: date ? fmt(date) : fmt(currentDate), employeeIds: employeeId ? [employeeId] : [] });
        resetComments();
        setDrawerOpen(true);
    };

    const openEdit = (task) => {
        setEditTask(task);
        setForm({
            title: task.title || '',
            description: task.description || '',
            projectId: task.projectId || null,
            employeeIds: task.assignedEmployees?.map(e => e.id) || [],
            taskDate: task.taskDate || '',
            startTime: task.startTime || '',
            endTime: task.endTime || '',
            location: task.location || '',
            status: task.status || 'PLANNED',
        });
        resetComments();
        setDrawerOpen(true);
        loadComments(task.id);
    };

    const handleAddComment = async () => {
        if (!newComment.trim() || !editTask) return;
        setCommentSaving(true);
        try {
            const created = await ProjectTaskService.addComment(editTask.id, newComment.trim());
            setComments(cs => [...cs, created]);
            setNewComment('');
        } catch { showToast('Failed to add comment', 'error'); }
        finally { setCommentSaving(false); }
    };

    const startEditComment = (comment) => {
        setEditingCommentId(comment.id);
        setEditingCommentText(comment.content);
    };

    const saveEditComment = async () => {
        if (!editingCommentText.trim() || !editTask) return;
        try {
            const updated = await ProjectTaskService.updateComment(editTask.id, editingCommentId, editingCommentText.trim());
            setComments(cs => cs.map(c => c.id === updated.id ? updated : c));
            setEditingCommentId(null);
            setEditingCommentText('');
        } catch { showToast('Failed to update comment', 'error'); }
    };

    const handleDeleteComment = async (commentId) => {
        if (!editTask) return;
        try {
            await ProjectTaskService.deleteComment(editTask.id, commentId);
            setComments(cs => cs.filter(c => c.id !== commentId));
        } catch { showToast('Failed to delete comment', 'error'); }
    };

    const handleSave = async () => {
        if (isEmployee) {
            setSaving(true);
            try {
                await ProjectTaskService.updateStatus(editTask.id, form.status);
                showToast('Status updated', 'success');
                setDrawerOpen(false);
                loadTasks();
            } catch { showToast('Failed to update status', 'error'); }
            finally { setSaving(false); }
            return;
        }
        if (!form.title.trim()) { showToast('Title is required', 'error'); return; }
        if (!form.taskDate) { showToast('Date is required', 'error'); return; }
        if (!form.projectId) { showToast('Project is required', 'error'); return; }
        setSaving(true);
        try {
            const payload = { ...form, startTime: form.startTime || null, endTime: form.endTime || null, description: form.description || null, location: form.location || null };
            if (editTask) { await ProjectTaskService.update(editTask.id, payload); showToast('Task updated', 'success'); }
            else { await ProjectTaskService.create(payload); showToast('Task created', 'success'); }
            setDrawerOpen(false);
            loadTasks();
        } catch { showToast('Failed to save task', 'error'); }
        finally { setSaving(false); }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            await ProjectTaskService.delete(deleteTarget.id);
            showToast('Task deleted', 'success');
            setConfirmOpen(false);
            setDeleteTarget(null);
            loadTasks();
        } catch { showToast('Failed to delete task', 'error'); }
    };

    const tasksForEmployee = (employeeId, date) =>
        tasks.filter(t => t.assignedEmployees?.some(e => e.id === employeeId) && (date ? t.taskDate === fmt(date) : true));

    const unassignedTasks = (date) =>
        tasks.filter(t => (!t.assignedEmployees || t.assignedEmployees.length === 0) && (date ? t.taskDate === fmt(date) : true));

    const goToday = () => setCurrentDate(new Date());
    const goPrev = () => setCurrentDate(d => viewMode === 'day' ? addDays(d, -1) : addDays(d, -7));
    const goNext = () => setCurrentDate(d => viewMode === 'day' ? addDays(d, 1) : addDays(d, 7));

    const weekDates = (() => { const m = startOfWeek(currentDate); return Array.from({ length: 7 }, (_, i) => addDays(m, i)); })();
    const headerLabel = viewMode === 'day' ? formatDisplayDate(currentDate) : `${formatShortDate(weekDates[0])} – ${formatShortDate(weekDates[6])}, ${weekDates[0].getFullYear()}`;

    const TaskChip = ({ task, conflicting, compact }) => {
        const colors = STATUS_COLORS[task.status] || STATUS_COLORS.PLANNED;
        return (
            <Box
                sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1, py: 0.4,
                    bgcolor: colors.bg,
                    border: `1px solid ${conflicting ? '#F59E0B' : colors.border}`,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    maxWidth: compact ? 150 : '100%',
                    boxShadow: conflicting ? '0 0 0 1px #F59E0B' : 'none',
                    '&:hover': { filter: 'brightness(0.96)' },
                }}
                onClick={() => openEdit(task)}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        {conflicting && <WarningAmberIcon sx={{ fontSize: 11, color: '#F59E0B', flexShrink: 0 }} />}
                        <Typography noWrap sx={{ fontSize: compact ? 11 : 12, fontWeight: 600, color: colors.text, lineHeight: 1.3 }}>
                            {task.title}
                        </Typography>
                    </Box>
                    {task.projectName && <Typography noWrap sx={{ fontSize: 10, color: '#64748B', lineHeight: 1.2 }}>{task.projectName}</Typography>}
                    {task.startTime && <Typography noWrap sx={{ fontSize: 10, color: '#94A3B8', lineHeight: 1.2 }}>{task.startTime}{task.endTime ? `–${task.endTime}` : ''}</Typography>}
                </Box>
                {!isEmployee && (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteTarget(task); setConfirmOpen(true); }}
                        sx={{ p: 0.2, color: '#CBD5E1', '&:hover': { color: '#EF4444' }, flexShrink: 0 }}>
                        <DeleteIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                )}
            </Box>
        );
    };

    const AddButton = ({ date, employeeId, small }) => (
        <Tooltip title="Add task">
            <IconButton size="small" onClick={() => openAdd(date, employeeId)}
                sx={{ width: small ? 22 : 26, height: small ? 22 : 26, bgcolor: '#F1F5F9', color: '#94A3B8', borderRadius: 1, flexShrink: 0, '&:hover': { bgcolor: '#427898', color: '#fff' } }}>
                <AddIcon sx={{ fontSize: small ? 13 : 15 }} />
            </IconButton>
        </Tooltip>
    );

    const DayView = () => (
        <Box>
            {employees.map((emp, idx) => {
                const empTasks = tasksForEmployee(emp.id, currentDate);
                const conflictIds = getConflictIds(empTasks);
                return (
                    <Box key={emp.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, px: 3, py: 1.5, borderBottom: idx < employees.length - 1 ? '1px solid #F1F5F9' : 'none', bgcolor: '#fff', '&:hover': { bgcolor: '#FAFBFC' } }}>
                        <Box sx={{ width: 160, flexShrink: 0, pt: 0.3 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>{emp.firstName} {emp.lastName}</Typography>
                            {emp.position && <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{emp.position}</Typography>}
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                            {empTasks.length === 0 && <Typography sx={{ fontSize: 12, color: '#CBD5E1', fontStyle: 'italic' }}>No tasks</Typography>}
                            {empTasks.map(t => <TaskChip key={t.id} task={t} conflicting={conflictIds.has(t.id)} />)}
                            <AddButton date={currentDate} employeeId={emp.id} />
                        </Box>
                    </Box>
                );
            })}
            {(() => {
                const uTasks = unassignedTasks(currentDate);
                if (!uTasks.length) return null;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, px: 3, py: 1.5, bgcolor: '#FAFBFC', borderTop: '1px solid #F1F5F9' }}>
                        <Box sx={{ width: 160, flexShrink: 0, pt: 0.3 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#94A3B8' }}>Unassigned</Typography>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                            {uTasks.map(t => <TaskChip key={t.id} task={t} conflicting={false} />)}
                        </Box>
                    </Box>
                );
            })()}
        </Box>
    );

    const WeekView = () => (
        <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 800 }}>
                <Box sx={{ display: 'flex', borderBottom: '1px solid #EEF2F6', bgcolor: '#F8FAFC' }}>
                    <Box sx={{ width: 160, flexShrink: 0, px: 3, py: 1 }} />
                    {weekDates.map((d, i) => {
                        const isToday = fmt(d) === fmt(new Date());
                        return (
                            <Box key={i} sx={{ flex: 1, px: 1.5, py: 1, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{WEEK_DAYS[i]}</Typography>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: isToday ? '#427898' : '#1E293B', ...(isToday && { bgcolor: '#EFF6FF', borderRadius: '50%', width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }) }}>
                                    {d.getDate()}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
                {employees.map((emp, idx) => (
                    <Box key={emp.id} sx={{ display: 'flex', alignItems: 'stretch', borderBottom: idx < employees.length - 1 ? '1px solid #F1F5F9' : 'none', bgcolor: '#fff', minHeight: 52 }}>
                        <Box sx={{ width: 160, flexShrink: 0, px: 3, py: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>{emp.firstName} {emp.lastName}</Typography>
                            {emp.position && <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{emp.position}</Typography>}
                        </Box>
                        {weekDates.map((d, di) => {
                            const dayTasks = tasksForEmployee(emp.id, d);
                            const conflictIds = getConflictIds(dayTasks);
                            return (
                                <Box key={di} sx={{ flex: 1, px: 1, py: 1, borderLeft: '1px solid #F1F5F9', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {dayTasks.map(t => <TaskChip key={t.id} task={t} conflicting={conflictIds.has(t.id)} compact />)}
                                    <AddButton date={d} employeeId={emp.id} small />
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        </Box>
    );

    const EmployeeDayView = () => {
        const conflictIds = getConflictIds(tasks);
        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, px: 3, py: 1.5, bgcolor: '#fff' }}>
                    <Box sx={{ width: 160, flexShrink: 0, pt: 0.3 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>My Tasks</Typography>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                        {tasks.length === 0 && <Typography sx={{ fontSize: 12, color: '#CBD5E1', fontStyle: 'italic' }}>No tasks assigned</Typography>}
                        {tasks.map(t => <TaskChip key={t.id} task={t} conflicting={conflictIds.has(t.id)} />)}
                    </Box>
                </Box>
            </Box>
        );
    };

    const EmployeeWeekView = () => (
        <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 800 }}>
                <Box sx={{ display: 'flex', borderBottom: '1px solid #EEF2F6', bgcolor: '#F8FAFC' }}>
                    <Box sx={{ width: 160, flexShrink: 0, px: 3, py: 1 }} />
                    {weekDates.map((d, i) => {
                        const isToday = fmt(d) === fmt(new Date());
                        return (
                            <Box key={i} sx={{ flex: 1, px: 1.5, py: 1, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{WEEK_DAYS[i]}</Typography>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: isToday ? '#427898' : '#1E293B', ...(isToday && { bgcolor: '#EFF6FF', borderRadius: '50%', width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }) }}>
                                    {d.getDate()}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'stretch', bgcolor: '#fff', minHeight: 52 }}>
                    <Box sx={{ width: 160, flexShrink: 0, px: 3, py: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>My Tasks</Typography>
                    </Box>
                    {weekDates.map((d, di) => {
                        const dayTasks = tasks.filter(t => t.taskDate === fmt(d));
                        const conflictIds = getConflictIds(dayTasks);
                        return (
                            <Box key={di} sx={{ flex: 1, px: 1, py: 1, borderLeft: '1px solid #F1F5F9', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {dayTasks.map(t => <TaskChip key={t.id} task={t} conflicting={conflictIds.has(t.id)} compact />)}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );

    const ReadField = ({ label, value }) => !value ? null : (
        <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>{label}</Typography>
            <Typography sx={{ fontSize: 14, color: '#1E293B' }}>{value}</Typography>
        </Box>
    );

    return (
        <Layout>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>

                {/* ── Page header ── */}
                <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #EEF2F6', px: { xs: 2, md: 3 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, position: 'sticky', top: 0, zIndex: 10 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton size="small" onClick={goPrev} sx={{ color: '#64748B' }}><ChevronLeftIcon /></IconButton>
                        <Button size="small" startIcon={<TodayIcon sx={{ fontSize: 15 }} />} onClick={goToday}
                            sx={{ textTransform: 'none', color: '#427898', fontWeight: 600, fontSize: 13 }}>Today</Button>
                        <IconButton size="small" onClick={goNext} sx={{ color: '#64748B' }}><ChevronRightIcon /></IconButton>
                        <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1E293B', ml: 0.5 }}>{headerLabel}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => { if (v) setViewMode(v); }} size="small"
                            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 13, px: 1.5, py: 0.5, color: '#64748B', borderColor: '#E2E8F0' }, '& .Mui-selected': { color: '#427898 !important', bgcolor: '#EFF6FF !important' } }}>
                            <ToggleButton value="day">Day</ToggleButton>
                            <ToggleButton value="week">Week</ToggleButton>
                        </ToggleButtonGroup>
                        {!isEmployee && (
                            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openAdd(null, null)}
                                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#427898', '&:hover': { bgcolor: '#356480' }, boxShadow: 'none', borderRadius: 1.5 }}>
                                New Task
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* ── Column header (day view) ── */}
                {viewMode === 'day' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 1, bgcolor: '#F8FAFC', borderBottom: '1px solid #EEF2F6' }}>
                        <Box sx={{ width: 160, flexShrink: 0 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Employee</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Tasks</Typography>
                    </Box>
                )}

                {/* ── Grid ── */}
                <Paper elevation={0} sx={{ mx: { xs: 1, md: 2 }, my: 2, border: '1px solid #EEF2F6', borderRadius: 2, overflow: 'hidden' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: '#427898' }} /></Box>
                    ) : isEmployee ? (
                        viewMode === 'day' ? <EmployeeDayView /> : <EmployeeWeekView />
                    ) : employees.length === 0 ? (
                        <Box sx={{ py: 6, textAlign: 'center' }}><Typography sx={{ color: '#94A3B8', fontSize: 14 }}>No employees found. Add employees first.</Typography></Box>
                    ) : viewMode === 'day' ? <DayView /> : <WeekView />}
                </Paper>
            </Box>

            {/* ── Side drawer ── */}
            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: DRAWER_WIDTH, display: 'flex', flexDirection: 'column' } }}>

                {/* Drawer header */}
                <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1E293B' }}>
                        {isEmployee ? 'Task Details' : editTask ? 'Edit Task' : 'New Task'}
                    </Typography>
                    <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: '#94A3B8' }}><CloseIcon fontSize="small" /></IconButton>
                </Box>

                {/* Drawer body — shared scroll container for both variants + comments */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* employee read-only view */}
                {isEmployee ? (
                    <>
                        <ReadField label="Title" value={editTask?.title} />
                        <ReadField label="Description" value={editTask?.description} />
                        <ReadField label="Project" value={editTask?.projectName} />
                        <ReadField label="Date" value={editTask?.taskDate} />
                        {(editTask?.startTime || editTask?.endTime) && (
                            <ReadField label="Time" value={[editTask.startTime, editTask.endTime].filter(Boolean).join(' – ')} />
                        )}
                        <ReadField label="Location" value={editTask?.location} />
                        {editTask?.assignedEmployees?.length > 0 && (
                            <Box>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>Assigned To</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {editTask.assignedEmployees.map(e => (
                                        <Chip key={e.id} label={`${e.firstName} ${e.lastName}`} size="small" sx={{ bgcolor: '#F1F5F9', color: '#475569', fontWeight: 500 }} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        <Divider />
                        <FormControl size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select value={form.status} label="Status" onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                                {['PLANNED', 'IN_PROGRESS', 'DONE'].map(v => (
                                    <MenuItem key={v} value={v}>{STATUS_LABELS[v]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                ) : (
                    /* admin edit form */
                    <>
                        <TextField label="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} size="small" fullWidth inputProps={{ maxLength: 200 }} />
                        <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} size="small" fullWidth multiline rows={2} />
                        <Autocomplete
                            options={projects}
                            getOptionLabel={o => o.name || ''}
                            value={projects.find(p => p.id === form.projectId) || null}
                            onChange={(_, v) => setForm(f => ({ ...f, projectId: v?.id || null }))}
                            size="small"
                            renderInput={params => <TextField {...params} label="Project *" error={!form.projectId && saving} helperText={!form.projectId && saving ? 'Project is required' : ''} />}
                        />
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <TextField label="Date *" type="date" value={form.taskDate} onChange={e => setForm(f => ({ ...f, taskDate: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                            <TextField label="Start" type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 110 }} />
                            <TextField label="End" type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 110 }} />
                        </Box>
                        <Autocomplete
                            multiple options={employees}
                            getOptionLabel={e => `${e.firstName} ${e.lastName}`}
                            value={employees.filter(e => form.employeeIds.includes(e.id))}
                            onChange={(_, v) => setForm(f => ({ ...f, employeeIds: v.map(e => e.id) }))}
                            size="small"
                            renderInput={params => <TextField {...params} label="Assigned employees" />}
                            renderTags={(value, getTagProps) => value.map((e, i) => (
                                <Chip key={e.id} label={`${e.firstName} ${e.lastName}`} size="small" {...getTagProps({ index: i })} />
                            ))}
                        />
                        <TextField label="Location (optional)" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} size="small" fullWidth inputProps={{ maxLength: 300 }} />
                        <FormControl size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select value={form.status} label="Status" onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                                {Object.entries(STATUS_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </>
                )}

                {/* Comments — shared by admin + employee, once the task exists */}
                {editTask && (
                    <>
                        <Divider />
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.25 }}>
                                Comments
                            </Typography>
                            {commentsLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={18} sx={{ color: '#427898' }} />
                                </Box>
                            ) : comments.length === 0 ? (
                                <Typography sx={{ fontSize: 13, color: '#CBD5E1' }}>No comments yet.</Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 1.5 }}>
                                    {comments.map(c => (
                                        <Box key={c.id} sx={{ borderRadius: 1.5, p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #EEF2F6' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#0F172A' }}>{c.authorName}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                                                        {c.createdAt ? new Date(c.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                                                    </Typography>
                                                    {c.canEdit && (
                                                        <IconButton size="small" onClick={() => startEditComment(c)} sx={{ p: 0.25, color: '#94A3B8', '&:hover': { color: '#427898' } }}>
                                                            <Typography sx={{ fontSize: 11 }}>Edit</Typography>
                                                        </IconButton>
                                                    )}
                                                    {c.canDelete && (
                                                        <IconButton size="small" onClick={() => handleDeleteComment(c.id)} sx={{ p: 0.25, color: '#CBD5E1', '&:hover': { color: '#EF4444' } }}>
                                                            <DeleteIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Box>
                                            {editingCommentId === c.id ? (
                                                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
                                                    <TextField
                                                        value={editingCommentText}
                                                        onChange={e => setEditingCommentText(e.target.value)}
                                                        size="small" fullWidth multiline
                                                    />
                                                    <Button size="small" onClick={saveEditComment} sx={{ textTransform: 'none' }}>Save</Button>
                                                    <Button size="small" onClick={() => setEditingCommentId(null)} sx={{ textTransform: 'none', color: '#64748B' }}>Cancel</Button>
                                                </Box>
                                            ) : (
                                                <Typography sx={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap' }}>{c.content}</Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                <TextField
                                    placeholder="Write a comment…"
                                    value={newComment}
                                    onChange={e => setNewComment(e.target.value)}
                                    size="small" fullWidth multiline maxRows={4}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAddComment}
                                    disabled={commentSaving || !newComment.trim()}
                                    sx={{ textTransform: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#356480' }, boxShadow: 'none', borderRadius: 1.5, flexShrink: 0 }}
                                >
                                    {commentSaving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Post'}
                                </Button>
                            </Box>
                        </Box>
                    </>
                )}
                </Box>

                {/* Drawer footer */}
                <Box sx={{ px: 3, py: 2.5, borderTop: '1px solid #EEF2F6', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0 }}>
                    <Button onClick={() => setDrawerOpen(false)} sx={{ textTransform: 'none', color: '#64748B' }}>
                        {isEmployee ? 'Close' : 'Cancel'}
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}
                        sx={{ textTransform: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#356480' }, boxShadow: 'none', borderRadius: 1.5, minWidth: 90 }}>
                        {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : isEmployee ? 'Update' : editTask ? 'Save' : 'Create'}
                    </Button>
                </Box>
            </Drawer>

            <ConfirmModal
                open={confirmOpen}
                onClose={() => { setConfirmOpen(false); setDeleteTarget(null); }}
                onConfirm={handleDeleteConfirm}
                question={`Delete "${deleteTarget?.title}"?`}
            />
        </Layout>
    );
}
